/**
 * 当前操作者的 Actor 上下文。
 *
 * 为什么需要它：家长与学生的权限判断都走 `shared/permissions.ts` 的
 * `canMutateContent(actor, content)` 这类函数，而 Actor 需要"家长对该学生的
 * 共享权限"（owner/editor/viewer）。这个信息只在学生详情接口里，
 * 所以用 Context 把它往下传，避免每个页面层层取 props。
 *
 * 关键点：家长视角在没有学生档案时默认 permission='viewer'（最保守），
 * 等档案加载完再升级。取最保守值可以防止"接口慢半拍时先闪出一个删除按钮"。
 */
import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';
import type { Actor, SharePermission } from '@fht/shared';
import { useAuth } from './auth';

export interface ActorContextValue {
  actor: Actor;
  /** 当前查看的学生 id（学生本人 = 自己的 userId）。 */
  studentId: string | null;
  /** 家长对该学生的权限；学生本人视角为 null。 */
  permission: SharePermission | null;
}

const FALLBACK: ActorContextValue = {
  actor: { kind: 'student', studentId: '' },
  studentId: null,
  permission: null,
};

const ActorContext = createContext<ActorContextValue>(FALLBACK);

/**
 * 以某个身份渲染一棵子树。
 *
 * ⚠️ **本组件目前全项目没有任何调用点**（2026-10-16 全仓 grep 确认）：它是当初为
 * "把家长对该学生的共享权限往子树里传"准备的接口，一直没接线，于是
 * `useActorContext()` 的消费者拿到的永远是下面的 `FALLBACK`。
 *
 * 之所以**保留而不是删除**：它不是"预览孩子端界面"那套（那套已整体删除），
 * 而是权限传递的唯一一个接缝 —— 删掉它，这条设计线索就只剩 `FALLBACK` 一个空壳，
 * 后来的人不会再知道"本来打算怎么把 permission 传下去"。要接线时在这里包一层即可
 * （`useStudentActor` / `useParentActor` 已经按它的形状写好了）。
 * 如果决定不要这条接缝，应当连同 `useActorContext` 与 `FALLBACK` 一起删，
 * 那是一次独立的重构 —— 不要在别的改动里顺手做。
 */
export function ActorProvider({
  kind,
  id,
  permission = null,
  children,
}: {
  /** 以谁的身份渲染这棵子树。 */
  kind: 'parent' | 'student';
  /** parent → 家长 userId；student → 学生 userId（= 学生账号的 user.id）。 */
  id: string;
  permission?: SharePermission | null;
  children: ReactNode;
}): ReactElement {
  const value = useMemo<ActorContextValue>(() => {
    // 家长未知权限时取最保守的 viewer
    const safePermission: SharePermission =
      permission === 'owner' || permission === 'editor' || permission === 'viewer'
        ? permission
        : 'viewer';
    const actor: Actor =
      kind === 'parent'
        ? { kind: 'parent', parentId: id, permission: safePermission }
        : { kind: 'student', studentId: id };
    return { actor, studentId: id, permission };
  }, [kind, id, permission]);
  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
}

export function useActorContext(): ActorContextValue {
  return useContext(ActorContext);
}

/**
 * 学生端的 Actor：永远是"学生本人"。
 *
 * 因为这个 Provider 从未被渲染（见 `ActorProvider` 的说明），拿到的是 `FALLBACK`：
 * `ctx.studentId` 为 `null`，于是回落到登录者自己的 `user.id`。
 */
export function useStudentActor(): Actor {
  const { user } = useAuth();
  const ctx = useActorContext();
  const studentId = ctx.studentId ?? user?.id ?? '';
  return useMemo<Actor>(() => ({ kind: 'student', studentId }), [studentId]);
}

/** 家长端使用的 Actor：带共享权限的 parent。 */
export function useParentActor(permission: SharePermission | null): Actor {
  const { user } = useAuth();
  const parentId = user?.id ?? '';
  const safePermission: SharePermission =
    permission === 'owner' || permission === 'editor' || permission === 'viewer'
      ? permission
      : 'viewer';
  return useMemo<Actor>(
    () => ({ kind: 'parent', parentId, permission: safePermission }),
    [parentId, safePermission],
  );
}

